/**
 * PP ACC Commentary Form Component
 */

import React, { useState, useEffect } from 'react';
import { PPCommentary, CommentaryType } from '@rti/shared/src/types';
import { ppaccAPI } from '@/services/commentary.service';
import RichTextEditor from '@/components/RichTextEditor';
import { useAuth } from '@/contexts/AuthContext';

interface PPACCFormProps {
  onSave?: (data: PPCommentary) => void;
  onCancel?: () => void;
  initialData?: Partial<PPCommentary>;
  commentaryType?: CommentaryType;
}

export const PPACCForm: React.FC<PPACCFormProps> = ({
  onSave,
  onCancel,
  initialData,
  commentaryType = 'preliminary',
}) => {
  const { user } = useAuth();

  const getPreviousMonth = () => {
    const now = new Date();
    const previousMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return {
      month: previousMonth.getMonth() + 1,
      year: previousMonth.getFullYear(),
    };
  };

  const { month: defaultMonth, year: defaultYear} = getPreviousMonth();

  const getLocalDateParts = (dateStr: string) => {
    const [year, month, day] = dateStr.split('T')[0].split('-');
    return { year: parseInt(year), month: parseInt(month), day: parseInt(day) };
  };

  const [selectedMonth, setSelectedMonth] = useState(
    initialData?.date ? getLocalDateParts(initialData.date).month : defaultMonth
  );
  const [selectedYear, setSelectedYear] = useState(
    initialData?.date ? getLocalDateParts(initialData.date).year : defaultYear
  );

  const [formData, setFormData] = useState<Partial<PPCommentary>>({
    date: initialData?.date || `${defaultYear}-${String(defaultMonth).padStart(2, '0')}-01`,
    pp_supply_demand: initialData?.pp_supply_demand || '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [currentRecordPendingChange, setCurrentRecordPendingChange] = useState<PPCommentary | null>(null);
  const [originalData, setOriginalData] = useState<Partial<PPCommentary>>({});
  const [isNewPendingRecord, setIsNewPendingRecord] = useState(false);

  useEffect(() => {
    if (initialData?.date) {
      const [year, month, day] = initialData.date.split('T')[0].split('-');
      setSelectedMonth(parseInt(month));
      setSelectedYear(parseInt(year));

      const checkForPendingChange = async () => {
        try {
          const pending = await ppaccAPI.getPending();
          const published = await ppaccAPI.getAll();

          const matchingPending = pending.find(p => p.date === initialData.date);
          const existsInPublished = published.some(pub => pub.date === initialData.date);

          if (matchingPending) {
            setCurrentRecordPendingChange(matchingPending);
            setIsNewPendingRecord(!existsInPublished);

            if (existsInPublished) {
              const publishedRecord = published.find(pub => pub.date === initialData.date);
              setOriginalData(publishedRecord || {});
            } else {
              setOriginalData({});
            }

            setFormData({
              date: matchingPending.date,
              pp_supply_demand: matchingPending.pp_supply_demand || '',
            });
          } else {
            setCurrentRecordPendingChange(null);
            setOriginalData({});
            setIsNewPendingRecord(false);
            setFormData({
              date: initialData.date,
              pp_supply_demand: initialData.pp_supply_demand || '',
            });
          }
        } catch (error) {
          console.error('Error checking pending changes:', error);
        }
      };

      checkForPendingChange();
    }
  }, [initialData]);

  useEffect(() => {
    const dateString = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-01`;
    setFormData(prev => ({ ...prev, date: dateString }));
  }, [selectedMonth, selectedYear]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const hasFieldChanged = (fieldName: keyof PPCommentary): boolean => {
    if (!currentRecordPendingChange || isNewPendingRecord || !originalData) {
      return false;
    }
    const currentValue = formData[fieldName];
    const originalValue = originalData[fieldName];
    return currentValue !== originalValue;
  };

  const cleanHtml = (html: string): string => {
    if (!html) return '';
    const text = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    return text ? html : '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!formData.date || !formData.pp_supply_demand) {
      setError('Date and PP Supply/Demand are required');
      return;
    }

    setLoading(true);
    try {
      const userName = user ? `${user.first_name} ${user.last_name}` : 'Unknown User';
      const dataToSave: PPCommentary = {
        ...formData,
        pp_supply_demand: cleanHtml(formData.pp_supply_demand || ''),
        last_updated_by: userName,
        last_updated_time: new Date().toISOString(),
      } as PPCommentary;

      await ppaccAPI.create(dataToSave, commentaryType);
      setSuccess(true);
      if (onSave) onSave(dataToSave);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save data');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    const { month, year } = getPreviousMonth();
    setSelectedMonth(month);
    setSelectedYear(year);
    setFormData({
      date: `${year}-${String(month).padStart(2, '0')}-01`,
      pp_supply_demand: '',
    });
    setError(null);
    setSuccess(false);
  };

  return (
    <div className="ppacc-form bg-rs-off-black text-rs-off-white p-6 rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-2xl font-bold text-rs-green">
          PPACC - Polypropylene Supply/Demand Commentary (Monthly){commentaryType === 'final' && ' - Final'}
        </h2>
        {currentRecordPendingChange && isNewPendingRecord && (
          <span className="px-3 py-1 bg-rs-salmon/20 text-rs-salmon text-sm rounded flex items-center gap-2">
            <span className="w-2 h-2 bg-rs-salmon rounded-full animate-pulse"></span>
            New Pending Record
          </span>
        )}
        {currentRecordPendingChange && !isNewPendingRecord && (
          <span className="px-3 py-1 bg-rs-salmon/20 text-rs-salmon text-sm rounded flex items-center gap-2">
            <span className="w-2 h-2 bg-rs-salmon rounded-full animate-pulse"></span>
            Pending Change
          </span>
        )}
      </div>
      {currentRecordPendingChange && (
        <p className="text-sm text-rs-off-white/60 mb-6">
          You are editing a pending change. Click "Save to Staging" to update it.
        </p>
      )}

      {error && (
        <div className="bg-red-900/20 border border-red-500 text-red-400 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-900/20 border border-green-500 text-green-400 px-4 py-3 rounded mb-4">
          Data saved successfully!
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        <div>
          <label className="block text-sm font-medium mb-2">Report Month *</label>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-[#dee7d9]/70 mb-1">Month</label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="w-full bg-[#1e1f1c] border border-[#2a2c28] rounded px-4 py-2 text-[#dee7d9] focus:border-[#67B640] focus:outline-none"
                required
              >
                <option value={1}>January</option>
                <option value={2}>February</option>
                <option value={3}>March</option>
                <option value={4}>April</option>
                <option value={5}>May</option>
                <option value={6}>June</option>
                <option value={7}>July</option>
                <option value={8}>August</option>
                <option value={9}>September</option>
                <option value={10}>October</option>
                <option value={11}>November</option>
                <option value={12}>December</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-[#dee7d9]/70 mb-1">Year</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="w-full bg-[#1e1f1c] border border-[#2a2c28] rounded px-4 py-2 text-[#dee7d9] focus:border-[#67B640] focus:outline-none"
                required
              >
                {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>
          <p className="text-xs text-[#dee7d9]/50 mt-2">
            Date will be set to: {formData.date}
          </p>
        </div>

        {/* PP Supply/Demand */}
        {currentRecordPendingChange && !isNewPendingRecord && originalData.pp_supply_demand && (
          <div className="bg-rs-dark-grey border border-rs-medium-grey rounded px-4 py-3">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-medium text-rs-off-white">Current Production - PP Supply/Demand:</span>
              <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded">Read-Only</span>
            </div>
            <div
              className="text-sm text-rs-off-white/80 p-3 bg-rs-off-black rounded border border-rs-medium-grey"
              dangerouslySetInnerHTML={{ __html: originalData.pp_supply_demand }}
            />
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium">
              PP Supply/Demand *
            </label>
            {currentRecordPendingChange && (
              <span className="px-2 py-0.5 bg-rs-salmon/20 text-rs-salmon text-xs rounded flex items-center gap-1">
                <span className="w-2 h-2 bg-rs-salmon rounded-full animate-pulse"></span>
                Pending Change
              </span>
            )}
          </div>
          <div className={hasFieldChanged('pp_supply_demand') ? 'ring-2 ring-rs-salmon/50 rounded' : ''}>
            <RichTextEditor
              value={formData.pp_supply_demand || ''}
              onChange={(value) => setFormData((prev) => ({ ...prev, pp_supply_demand: value }))}
              placeholder={currentRecordPendingChange ? "This is your pending change..." : "Enter Polypropylene supply/demand commentary..."}
              rows={8}
            />
          </div>
        </div>

        <div className="flex gap-4 pt-4">
          <button
            type="submit"
            disabled={loading}
            className="bg-[#67B640] hover:bg-[#7ac950] !text-[#161814] font-semibold px-6 py-2 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Saving...' : 'Save to Staging'}
          </button>
          <button
            type="button"
            onClick={handleClear}
            className="bg-[#2a2c28] hover:bg-[#3a3c38] text-[#dee7d9] font-semibold px-6 py-2 rounded transition-colors"
          >
            Clear
          </button>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="bg-[#1e1f1c] hover:bg-[#2a2c28] text-[#dee7d9] font-semibold px-6 py-2 rounded transition-colors"
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default PPACCForm;

